import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { trackPageView, setUserId } from "./utils/analytics";
import LandingPage from "./pages/LandingPage.jsx";
import AuthPage from "./pages/AuthPage";
import OnboardingPage from "./pages/OnboardingPage";
import ChatPage from "./pages/ChatPage.jsx";
import EmailsPage from "./pages/EmailsPage.jsx";
import PricingPage from "./pages/PricingPage.jsx";
import BillingSuccessPage from "./pages/BillingSuccessPage.jsx";
import AdminPage from "./pages/AdminPage.jsx";
import TermsPage from "./pages/TermsPage.jsx";
import PrivacyPage from "./pages/PrivacyPage.jsx";
import SharedConversationPage from "./pages/SharedConversationPage.jsx";
import DocsPage from "./pages/DocsPage.jsx";
import RemindersPage from "./pages/RemindersPage.jsx";
import LoadingScreen from "./components/LoadingScreen";

// Page titles for analytics
const pageTitles = {
  '/': 'Home - Arlo',
  '/auth': 'Sign In - Arlo',
  '/auth/verify': 'Verify Email - Arlo',
  '/auth/reset-password': 'Reset Password - Arlo',
  '/onboarding': 'Get Started - Arlo',
  '/chat': 'Chat - Arlo',
  '/emails': 'Emails - Arlo',
  '/reminders': 'Reminders - Arlo',
  '/pricing': 'Pricing - Arlo',
  '/billing/success': 'Payment Success - Arlo',
  '/admin': 'Admin Dashboard - Arlo',
  '/terms': 'Terms of Use - Arlo',
  '/privacy': 'Privacy Policy - Arlo',
  '/docs': 'Documentation - Arlo'
};

// Analytics tracker component
const AnalyticsTracker = () => {
  const location = useLocation();
  const { user } = useAuth();
  
  useEffect(() => {
    // Track page view on route change
    const path = location.pathname;
    const title = pageTitles[path] || `Arlo - ${path}`;
    trackPageView(path, title);
  }, [location]);
  
  useEffect(() => {
    // Set user ID when logged in
    if (user?.id) {
      setUserId(user.id);
    }
  }, [user]);
  
  return null;
};

// Protected route wrapper
const ProtectedRoute = ({ children, requireOnboarding = true }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <LoadingScreen />;
  }
  
  if (!user) {
    return <Navigate to="/auth" replace />;
  }
  
  if (requireOnboarding && !user.onboarding_completed) {
    return <Navigate to="/onboarding" replace />;
  }
  
  return children;
};

// Onboarding route - requires auth but not onboarding completion
const OnboardingRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <LoadingScreen />;
  }
  
  if (!user) {
    return <Navigate to="/auth" replace />;
  }
  
  if (user.onboarding_completed) {
    return <Navigate to="/chat" replace />;
  }
  
  return children;
};

// Public route - redirect if already logged in
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <LoadingScreen />;
  }
  
  if (user) {
    if (!user.onboarding_completed) {
      return <Navigate to="/onboarding" replace />;
    }
    return <Navigate to="/chat" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route 
        path="/auth" 
        element={
          <PublicRoute>
            <AuthPage />
          </PublicRoute>
        } 
      />
      <Route 
        path="/auth/verify" 
        element={<AuthPage />} 
      />
      <Route 
        path="/auth/reset-password" 
        element={<AuthPage />} 
      />
      <Route 
        path="/onboarding" 
        element={
          <OnboardingRoute>
            <OnboardingPage />
          </OnboardingRoute>
        } 
      />
      <Route 
        path="/chat" 
        element={
          <ProtectedRoute>
            <ChatPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/emails" 
        element={
          <ProtectedRoute>
            <EmailsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/reminders" 
        element={
          <ProtectedRoute>
            <RemindersPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/pricing" 
        element={<PricingPage />} 
      />
      <Route 
        path="/billing/success" 
        element={
          <ProtectedRoute>
            <BillingSuccessPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin" 
        element={
          <ProtectedRoute>
            <AdminPage />
          </ProtectedRoute>
        } 
      />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/docs" element={<DocsPage />} />
      <Route path="/shared/:shareId" element={<SharedConversationPage />} />
      <Route path="/" element={<LandingPage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AnalyticsTracker />
        <AppRoutes />
        <Toaster 
          position="top-center" 
          richColors 
          toastOptions={{
            style: {
              background: 'hsl(240 10% 6%)',
              border: '1px solid hsl(240 3.7% 15.9%)',
              color: 'hsl(0 0% 98%)',
            },
          }}
        />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
